/**
 * Writes a report model (integrations/mdReport.js, with each section's picture
 * attached as `section.image = { dataUrl, width, height }`) to a PDF or a
 * PowerPoint file. The libraries (jsPDF, PptxGenJS) are loaded on demand, so
 * they are not in the main bundle.
 *
 * Both writers return `{ data: ArrayBuffer, mime, fileName }`; the caller
 * downloads it (ui_utils.downloadData). They work in Node as well as the
 * browser, which is how they are unit tested.
 */
import { MD_REPORT_FORMATS, reportFileName } from './mdReport';
import { REPORT_COMPANY, REPORT_NOTICES, reportCopyright } from './mdReportBranding';

// PDF layout, millimetres on A4 portrait. Every page carries the logo at the
// top left and the footer bar; the body sits between them.
const PDF_MARGIN = 15;
const PDF_WIDTH = 210;
const PDF_HEIGHT = 297;
const PDF_BODY_WIDTH = PDF_WIDTH - 2 * PDF_MARGIN;
const PDF_LINE = 4.2;
const PDF_LOGO_WIDTH = 34;
const PDF_LOGO_TOP = 6;
const PDF_TOP = 28;
const PDF_FOOTER_HEIGHT = 7;
const PDF_BOTTOM = PDF_HEIGHT - PDF_FOOTER_HEIGHT - 6;

/**
 * jsPDF's built-in fonts cover Latin-1 only; anything else would print as
 * garbage. Replace the few non-Latin-1 characters reports use, and any other
 * with '?'.
 */
export function pdfSafe(text) {
    return String(text)
        .replace(/[–—]/g, '-')
        .replace(/α/g, 'alpha')
        .replace(/≥/g, '>=')
        .replace(/≤/g, '<=')
        .replace(/[Ā-￿]/g, '?');
}

function pdfLines(doc, text, width) {
    return doc.splitTextToSize(pdfSafe(text), width);
}

/**
 * @param {object} model - from buildReportModel, with `image` on each section
 * @returns {Promise<{ data: ArrayBuffer, mime: string, fileName: string }>}
 */
export async function buildPdf(model) {
    const { jsPDF } = await import('jspdf');
    const { LOGO_PNG, FOOTER_BAR_PNG } = await import('./mdReportLogo');
    // eslint-disable-next-line new-cap
    const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
    let y = PDF_TOP;

    function ensure(height) {
        if (y + height > PDF_BOTTOM) {
            doc.addPage();
            y = PDF_TOP;
        }
    }

    function paragraph(text, {
        size = 9, bold = false, gap = 1, gray = false,
    } = {}) {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(size);
        doc.setTextColor(gray ? 90 : 0);
        const lines = pdfLines(doc, text, PDF_BODY_WIDTH);
        const height = lines.length * (size * 0.4) + gap;
        ensure(height);
        doc.text(lines, PDF_MARGIN, y + size * 0.35);
        doc.setTextColor(0);
        y += height;
    }

    // The job's name is the headline; the kind of report sits under it.
    if (model.jobName) {
        paragraph(model.jobName, { size: 20, bold: true, gap: 1.5 });
        paragraph(model.title, { size: 13, gap: 2.5 });
    } else {
        paragraph(model.title, { size: 20, bold: true, gap: 3 });
    }
    if (model.generated) paragraph(`Generated ${model.generated}`, { size: 9, gap: 3 });
    for (const line of model.details) paragraph(line, { size: 9, gap: 0.5 });
    if (REPORT_NOTICES.length) {
        y += 4;
        for (const notice of REPORT_NOTICES) paragraph(notice, { size: 8, gap: 1.5, gray: true });
    }

    for (const compound of model.compounds) {
        doc.addPage();
        y = PDF_TOP;
        paragraph(compound.name, { size: 15, bold: true, gap: 2 });
        for (const line of compound.summary) paragraph(line, { size: 9, gap: 0.5 });
        y += 3;
        for (const section of compound.sections) {
            const image = section.image;
            const imageHeight = image ? (PDF_BODY_WIDTH * image.height) / image.width : 0;
            doc.setFontSize(8.5);
            const captionLines = pdfLines(doc, section.caption || '', PDF_BODY_WIDTH);
            ensure(8 + imageHeight + captionLines.length * 3.6 + 4);
            paragraph(section.title, { size: 11, bold: true, gap: 2 });
            if (image) {
                doc.addImage(image.dataUrl, 'PNG', PDF_MARGIN, y, PDF_BODY_WIDTH, imageHeight);
                y += imageHeight + 2;
            }
            if (captionLines.length) {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8.5);
                doc.text(captionLines, PDF_MARGIN, y + 3);
                y += captionLines.length * 3.6 + 4;
            }
        }
    }

    const pages = doc.getNumberOfPages();
    const logoHeight = (PDF_LOGO_WIDTH * LOGO_PNG.height) / LOGO_PNG.width;
    const copyright = pdfSafe(reportCopyright());
    for (let page = 1; page <= pages; page += 1) {
        doc.setPage(page);
        doc.addImage(LOGO_PNG.dataUrl, 'PNG', PDF_MARGIN - 3, PDF_LOGO_TOP, PDF_LOGO_WIDTH, logoHeight);
        doc.addImage(
            FOOTER_BAR_PNG.dataUrl, 'PNG', 0, PDF_HEIGHT - PDF_FOOTER_HEIGHT, PDF_WIDTH, PDF_FOOTER_HEIGHT,
        );
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(0);
        doc.text(copyright, PDF_MARGIN, PDF_HEIGHT - 2.6);
        doc.text(`Page ${page} of ${pages}`, PDF_WIDTH - PDF_MARGIN, PDF_HEIGHT - 2.6, { align: 'right' });
    }

    const format = MD_REPORT_FORMATS.find((f) => f.key === 'pdf');
    return {
        data: doc.output('arraybuffer'),
        mime: format.mime,
        fileName: reportFileName(model.jobName, format.extension),
    };
}

// PowerPoint layout, inches, on the company slide master's 13.333 x 7.5 slide:
// logo top left, a title to its right above a rule at y 1.03, the footer bar
// along the bottom edge.
const PPT_WIDTH = 13.333;
const PPT_MARGIN = 0.6;
const PPT_BODY_WIDTH = PPT_WIDTH - 2 * PPT_MARGIN;
const PPT_LOGO = { x: 0.123, y: 0.149, w: 2.058 };
const PPT_TITLE = {
    x: 2.52, y: 0.25, w: 10, h: 0.75,
};
const PPT_RULE_Y = 1.026;
const PPT_BODY_TOP = 1.3;
const PPT_FOOTER_Y = 7.11;
const PPT_BODY_BOTTOM = 7.0;
const PPT_IMAGE_MAX_HEIGHT = 4.4;
// A chart taller than this aspect (width / height) is laid out beside its caption.
const PPT_TALL_ASPECT = 1.6;
const PPT_TALL_IMAGE_HEIGHT = PPT_BODY_BOTTOM - PPT_BODY_TOP - 0.1;
const MASTER_NAME = 'CONIFER_POINT';

/**
 * @param {object} model - from buildReportModel, with `image` on each section
 * @returns {Promise<{ data: ArrayBuffer, mime: string, fileName: string }>}
 */
export async function buildPptx(model) {
    const { default: PptxGenJS } = await import('pptxgenjs');
    const { LOGO_PNG, FOOTER_BAR_PNG } = await import('./mdReportLogo');
    const pptx = new PptxGenJS();
    pptx.layout = 'LAYOUT_WIDE';
    pptx.title = `${model.title}: ${model.jobName || ''}`.trim();
    pptx.company = REPORT_COMPANY;

    // The slide master: logo, footer bar with the copyright line, slide number.
    pptx.defineSlideMaster({
        title: MASTER_NAME,
        background: { color: 'FFFFFF' },
        objects: [
            {
                image: {
                    data: LOGO_PNG.dataUrl,
                    x: PPT_LOGO.x,
                    y: PPT_LOGO.y,
                    w: PPT_LOGO.w,
                    h: (PPT_LOGO.w * LOGO_PNG.height) / LOGO_PNG.width,
                },
            },
            {
                image: {
                    data: FOOTER_BAR_PNG.dataUrl,
                    x: 0,
                    y: PPT_FOOTER_Y,
                    w: PPT_WIDTH,
                    h: 7.5 - PPT_FOOTER_Y,
                },
            },
            {
                text: {
                    text: reportCopyright(),
                    options: {
                        x: 0.05,
                        y: PPT_FOOTER_Y + 0.05,
                        w: 5,
                        h: 0.3,
                        fontSize: 8,
                        color: '000000',
                        fontFace: 'Calibri',
                    },
                },
            },
        ],
        slideNumber: {
            x: 12.3, y: PPT_FOOTER_Y + 0.05, w: 0.9, h: 0.3, fontSize: 10, color: '000000', align: 'right',
        },
    });
    const addSlide = () => pptx.addSlide({ masterName: MASTER_NAME });
    // A slide title in the master's title area, with its rule beneath.
    const titled = (text) => {
        const slide = addSlide();
        slide.addText(text, {
            ...PPT_TITLE, fontSize: 24, bold: true, valign: 'bottom', fit: 'shrink',
        });
        slide.addShape(pptx.ShapeType.line, {
            x: PPT_TITLE.x, y: PPT_RULE_Y, w: PPT_TITLE.w, h: 0, line: { color: '000000', width: 1.5 },
        });
        return slide;
    };

    const title = addSlide();
    // The job's name is the headline; the kind of report sits under it.
    title.addText(model.jobName || model.title, {
        x: 1.67, y: 1.9, w: 10, h: 1.1, fontSize: 40, bold: true, align: 'center', fit: 'shrink',
    });
    if (model.jobName) {
        title.addText(model.title, {
            x: 1.67, y: 3.1, w: 10, h: 0.7, fontSize: 24, align: 'center',
        });
    }
    const detailLines = [...(model.generated ? [`Generated ${model.generated}`] : []), ...model.details];
    if (detailLines.length) {
        title.addText(detailLines.join('\n'), {
            x: 1.67, y: 3.95, w: 10, h: 2.0, fontSize: 14, valign: 'top', align: 'center',
        });
    }
    if (REPORT_NOTICES.length) {
        title.addText(REPORT_NOTICES.join('\n'), {
            x: PPT_MARGIN,
            y: 6.05,
            w: PPT_BODY_WIDTH,
            h: 0.95,
            fontSize: 9,
            color: '595959',
            valign: 'bottom',
            align: 'center',
        });
    }

    for (const compound of model.compounds) {
        const summary = titled(compound.name);
        if (compound.summary.length) {
            summary.addText(compound.summary.join('\n'), {
                x: PPT_MARGIN, y: PPT_BODY_TOP, w: PPT_BODY_WIDTH, h: 3.5, fontSize: 18, valign: 'top',
            });
        }
        for (const section of compound.sections) {
            const slide = titled(`${compound.name}: ${section.title}`);
            const aspect = section.image ? section.image.width / section.image.height : 0;
            if (section.image && aspect < PPT_TALL_ASPECT) {
                // A tall chart: the picture on the left, its caption on the right.
                const height = PPT_TALL_IMAGE_HEIGHT;
                const width = height * aspect;
                slide.addImage({
                    data: section.image.dataUrl,
                    x: PPT_MARGIN,
                    y: PPT_BODY_TOP,
                    w: width,
                    h: height,
                });
                if (section.caption) {
                    slide.addText(section.caption, {
                        x: PPT_MARGIN + width + 0.3,
                        y: PPT_BODY_TOP,
                        w: PPT_WIDTH - 2 * PPT_MARGIN - width - 0.3,
                        h: height,
                        fontSize: 11,
                        valign: 'top',
                    });
                }
            } else {
                let imageBottom = PPT_BODY_TOP;
                if (section.image) {
                    const height = Math.min(PPT_IMAGE_MAX_HEIGHT, PPT_BODY_WIDTH / aspect);
                    const width = height * aspect;
                    slide.addImage({
                        data: section.image.dataUrl,
                        x: (PPT_WIDTH - width) / 2,
                        y: PPT_BODY_TOP,
                        w: width,
                        h: height,
                    });
                    imageBottom = PPT_BODY_TOP + height;
                }
                if (section.caption) {
                    slide.addText(section.caption, {
                        x: PPT_MARGIN,
                        y: imageBottom + 0.1,
                        w: PPT_BODY_WIDTH,
                        h: PPT_BODY_BOTTOM - imageBottom - 0.1,
                        fontSize: 10,
                        valign: 'top',
                    });
                }
            }
        }
    }

    const format = MD_REPORT_FORMATS.find((f) => f.key === 'pptx');
    return {
        data: await pptx.write({ outputType: 'arraybuffer' }),
        mime: format.mime,
        fileName: reportFileName(model.jobName, format.extension),
    };
}

/**
 * @param {object} model
 * @param {'pdf'|'pptx'} format
 * @returns {Promise<{ data: ArrayBuffer, mime: string, fileName: string }>}
 */
export async function writeReport(model, format) {
    if (format === 'pdf') return buildPdf(model);
    if (format === 'pptx') return buildPptx(model);
    throw new Error(`Unknown report format: ${format}`);
}
